import { cn } from '@glade-ai/ui';
import Image from 'next/image';
import { type MouseEventHandler, type PropsWithChildren } from 'react';

import CustomLink from '@/components/CustomLink';
import NoodleAI from '@/components/Icons/NoodleAI';
import PACERLogo from '@/lib/Creators/Dashboard/Integrations/assets/pacer.png';

import s from './Button.module.scss';
import Progress from './Progress';

type BaseProps = {
  disabled?: boolean;
  loading?: boolean;
  variant: 'primary' | 'secondary' | 'destructive' | 'attention' | 'ai' | 'editable' | 'pacer';
  size: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
};

type ButtonProps =
  | {
      href?: never;
      newTab?: never;
      onClick: MouseEventHandler<HTMLButtonElement>;
      type: 'button';
    }
  | {
      href?: never;
      newTab?: never;
      onClick?: MouseEventHandler<HTMLButtonElement>;
      type?: 'submit' | 'reset';
    };

type LinkProps = {
  href: string;
  newTab?: boolean;
  onClick?: never;
  type?: never;
};

type Props = BaseProps & (ButtonProps | LinkProps);

const Button: React.FC<PropsWithChildren<Props>> = ({
  children,
  className,
  disabled = false,
  loading,
  variant = 'primary',
  size = 'md',
  onClick,
  href,
  fullWidth,
  type = 'button',
  newTab,
}) => {
  const wrapperClass = cn(
    s.wrapper,
    'focus-ring',
    variant === 'primary' && s.primary,
    (variant === 'secondary' || variant === 'pacer') && s.secondary,
    variant === 'destructive' && s.destructive,
    variant === 'attention' && s.attention,
    variant === 'ai' && s.ai,
    size === 'xs' && s.xs,
    size === 'sm' && s.sm,
    size === 'md' && s.md,
    size === 'lg' && s.lg,
    loading && s.loading,
    fullWidth && s.fullWidth,
    className,
  );

  if (href) {
    return (
      <CustomLink className={wrapperClass} to={href} newTab={newTab}>
        {loading && <Progress size={size} />}
        {children}
      </CustomLink>
    );
  }

  return (
    <button className={wrapperClass} disabled={disabled || loading} type={type} onClick={onClick}>
      {variant === 'ai' && (
        <>
          <div className={s.aiBorder} />
          <div className={s.aiBg} />
        </>
      )}
      {variant === 'pacer' && !loading && (
        <Image src={PACERLogo} alt='Pacer' width={20} height={20} style={{ marginRight: 8 }} />
      )}
      {loading && <Progress size={size} />}
      <span className={cn(variant === 'ai' ? s.aiChildren : s.contents)}>
        {variant === 'ai' && !loading && <NoodleAI size={size === 'sm' ? 16 : 24} />}
        {loading && variant === 'ai' ? <span className='ai-label'>{children}</span> : <>{children}</>}
      </span>
    </button>
  );
};

export default Button;
